/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useCallback } from 'react';
import './CloudServicesDashboard.css';

// ═══════════════════════════════════════════════════════════════════════════
// API Configuration - Production/Development detection
// ═══════════════════════════════════════════════════════════════════════════
const PRODUCTION_API_BASE = 'https://skilldelivery-platform.onrender.com';
const DEVELOPMENT_API_BASE = 'http://localhost:5000';

const isProduction = typeof window !== 'undefined' && 
                     window.location.hostname !== 'localhost' &&
                     window.location.hostname !== '127.0.0.1';

const API_URL = isProduction ? PRODUCTION_API_BASE : DEVELOPMENT_API_BASE;

// Admin auth also needs the 2FA session header when the admin has 2FA on.
// adminApi.js adds it for axios calls; plain fetch calls must add it too.
const twoFAHeader = () => {
  const s = localStorage.getItem('admin2FASession');
  return s ? { 'x-2fa-session': s } : {};
};

console.log('[CloudServicesDashboard] API Base:', API_URL);

// Service icons and colors
const SERVICE_CONFIG = {
  neo4j: { icon: '🔗', color: '#018BFF', label: 'Neo4j Aura' },
  render: { icon: '🚀', color: '#46E3B7', label: 'Render' },
  mongodb: { icon: '🍃', color: '#00ED64', label: 'MongoDB Atlas' },
  vercel: { icon: '▲', color: '#000000', label: 'Vercel' },
  huggingface: { icon: '🤗', color: '#FFD21E', label: 'HuggingFace' },
  modal: { icon: '⚡', color: '#7C3AED', label: 'Modal' },
  kaggle: { icon: '📊', color: '#20BEFF', label: 'Kaggle' },
  // NEW: Additional AI and Database Services
  neon: { icon: '🐘', color: '#3ECF8E', label: 'Neon PostgreSQL' },
  groq: { icon: '⚡', color: '#F55036', label: 'Groq' },
  gemini: { icon: '💎', color: '#4285F4', label: 'Google Gemini' },
  anthropic: { icon: '🧠', color: '#D97706', label: 'Anthropic Claude' },
  openai: { icon: '🌐', color: '#10A37F', label: 'OpenAI' }
};

const CloudServicesDashboard = () => {
  const [services, setServices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminAccessToken');
      const headers = {
        'Authorization': `Bearer ${token}`, ...twoFAHeader()
      };

      const [servicesRes, summaryRes, alertsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/cloud-costs`, { headers }),
        fetch(`${API_URL}/api/admin/cloud-costs/summary`, { headers }),
        fetch(`${API_URL}/api/admin/cloud-costs/alerts`, { headers })
      ]);

      const servicesData = await servicesRes.json();
      const summaryData = await summaryRes.json();
      const alertsData = await alertsRes.json();

      if (servicesData.success) setServices(servicesData.services);
      if (summaryData.success) setSummary(summaryData);
      if (alertsData.success) setAlerts(alertsData.alerts);
      
      setError(null);
    } catch (err) {
      setError('Failed to fetch cloud services data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Handle cost update
  const handleUpdateCost = async (serviceId) => {
    try {
      const token = localStorage.getItem('adminAccessToken');
      const response = await fetch(`${API_URL}/api/admin/cloud-costs/${serviceId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`, ...twoFAHeader()
        },
        body: JSON.stringify(editForm)
      });

      const result = await response.json();
      if (result.success) {
        setEditingService(null);
        setEditForm({});
        fetchData();
      } else {
        alert(`Failed to update: ${result.error}`);
      }
    } catch (err) {
      alert('Error updating cost');
      console.error(err);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get status badge class
  const getStatusClass = (status) => {
    switch (status) {
      case 'healthy': return 'status-healthy';
      case 'warning': return 'status-warning';
      case 'critical': return 'status-critical';
      default: return 'status-unknown';
    }
  };

  // Get tier badge class
  const getTierClass = (tier) => {
    switch (tier) {
      case 'free': return 'tier-free';
      case 'hobby': return 'tier-free';
      case 'starter': return 'tier-free';
      case 'professional': return 'tier-paid';
      case 'pro': return 'tier-paid';
      case 'team': return 'tier-paid';
      default: return 'tier-unknown';
    }
  };

  if (loading) {
    return (
      <div className="cloud-dashboard loading">
        <div className="loading-spinner"></div>
        <p>Loading cloud services...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cloud-dashboard error">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
        <button onClick={fetchData}>Retry</button>
      </div>
    );
  }

  return (
    <div className="cloud-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1>☁️ Cloud Services</h1>
          <p className="subtitle">Platform infrastructure costs and status</p>
        </div>
        <div className="header-right">
          <button className="refresh-btn" onClick={fetchData}>
            🔄 Refresh
          </button>
          <span className="last-updated">
            Updated: {formatDate(summary?.lastUpdated)}
          </span>
        </div>
      </div>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <div className="alerts-banner">
          <div className="alerts-header">
            <span className="alert-icon">⚠️</span>
            <span>{alerts.length} Alert{alerts.length > 1 ? 's' : ''}</span>
          </div>
          <div className="alerts-list">
            {alerts.slice(0, 3).map((alert, idx) => (
              <div key={idx} className={`alert-item alert-${alert.level}`}>
                <span className="alert-service">{alert.serviceName}</span>
                <span className="alert-message">{alert.message}</span>
                <a href={alert.action?.includes('http') ? alert.action : '#'} 
                   target="_blank" rel="noopener noreferrer"
                   className="alert-action">
                  View →
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card total">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <div className="card-label">Current Month</div>
            <div className="card-value">{formatCurrency(summary?.totalCurrent)}</div>
          </div>
        </div>
        <div className="summary-card projected">
          <div className="card-icon">📈</div>
          <div className="card-content">
            <div className="card-label">Projected</div>
            <div className="card-value">{formatCurrency(summary?.totalProjected)}</div>
          </div>
        </div>
        <div className="summary-card services-count">
          <div className="card-icon">🔧</div>
          <div className="card-content">
            <div className="card-label">Active Services</div>
            <div className="card-value">{services.length}</div>
          </div>
        </div>
        <div className="summary-card savings">
          <div className="card-icon">💚</div>
          <div className="card-content">
            <div className="card-label">Monthly Savings</div>
            <div className="card-value">{formatCurrency(summary?.monthlySavings || 143)}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`tab ${activeTab === 'by-category' ? 'active' : ''}`}
          onClick={() => setActiveTab('by-category')}
        >
          📂 By Category
        </button>
        <button 
          className={`tab ${activeTab === 'update' ? 'active' : ''}`}
          onClick={() => setActiveTab('update')}
        >
          ✏️ Update Costs
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="services-grid">
            {services.map(service => {
              const config = SERVICE_CONFIG[service.id] || {};
              return (
                <div key={service.id} className="service-card">
                  <div className="service-header" style={{ borderColor: config.color }}>
                    <span className="service-icon">{config.icon}</span>
                    <div className="service-info">
                      <h3>{service.name}</h3>
                      <span className={`tier-badge ${getTierClass(service.tier)}`}>
                        {service.tier}
                      </span>
                    </div>
                    <span className={`status-badge ${getStatusClass(service.status)}`}>
                      {service.status}
                    </span>
                  </div>
                  
                  <div className="service-costs">
                    <div className="cost-item">
                      <span className="cost-label">Current</span>
                      <span className="cost-value">{formatCurrency(service.currentCost?.currentAmount)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="cost-label">Projected</span>
                      <span className="cost-value">{formatCurrency(service.currentCost?.projectedAmount)}</span>
                    </div>
                  </div>
                  
                  <div className="service-meta">
                    <div className="billing-info">
                      <span className="billing-label">Billing:</span>
                      <span className="billing-cycle">{service.billingCycle}</span>
                    </div>
                    <div className="next-billing">
                      <span className="next-label">Next:</span>
                      <span className="next-date">
                        {formatDate(service.nextBillingDate)}
                      </span>
                    </div>
                    <a 
                      href={service.consoleUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="console-link"
                    >
                      Console →
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'by-category' && summary?.byCategory && (
          <div className="categories-view">
            {Object.entries(summary.byCategory).map(([category, data]) => (
              <div key={category} className="category-section">
                <div className="category-header">
                  <h3>
                    {category === 'database' && '🗄️ Databases'}
                    {category === 'hosting' && '🌐 Hosting'}
                    {category === 'ml' && '🤖 ML & AI'}
                  </h3>
                  <div className="category-totals">
                    <span>Current: {formatCurrency(data.totalCurrent)}</span>
                    <span>Projected: {formatCurrency(data.totalProjected)}</span>
                  </div>
                </div>
                <div className="category-services">
                  {data.services.map(svc => (
                    <div key={svc.id} className="category-service-row">
                      <span className="svc-icon">{SERVICE_CONFIG[svc.id]?.icon}</span>
                      <span className="svc-name">{svc.name}</span>
                      <span className={`svc-tier ${getTierClass(svc.tier)}`}>{svc.tier}</span>
                      <span className="svc-cost">{formatCurrency(svc.currentCost)}</span>
                      <span className={`svc-status ${getStatusClass(svc.status)}`}>
                        {svc.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'update' && (
          <div className="update-costs-view">
            <p className="update-instructions">
              💡 Since most cloud providers don't offer billing APIs, you can manually update 
              costs here after checking each service's console.
            </p>
            
            <div className="update-grid">
              {services.map(service => {
                const config = SERVICE_CONFIG[service.id] || {};
                const isEditing = editingService === service.id;
                
                return (
                  <div key={service.id} className="update-card">
                    <div className="update-card-header">
                      <span className="service-icon">{config.icon}</span>
                      <span className="service-name">{service.name}</span>
                      <a 
                        href={service.consoleUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="check-console-btn"
                      >
                        Check Console ↗
                      </a>
                    </div>
                    
                    {isEditing ? (
                      <div className="edit-form">
                        <div className="form-row">
                          <label>Current Cost ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={editForm.currentAmount || ''}
                            onChange={e => setEditForm({...editForm, currentAmount: e.target.value})}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="form-row">
                          <label>Projected Cost ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={editForm.projectedAmount || ''}
                            onChange={e => setEditForm({...editForm, projectedAmount: e.target.value})}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="form-row">
                          <label>Usage %</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={editForm.usagePercent || ''}
                            onChange={e => setEditForm({...editForm, usagePercent: e.target.value})}
                            placeholder="0"
                          />
                        </div>
                        <div className="form-row">
                          <label>Tier</label>
                          <select
                            value={editForm.tier || service.tier}
                            onChange={e => setEditForm({...editForm, tier: e.target.value})}
                          >
                            <option value="free">Free</option>
                            <option value="hobby">Hobby</option>
                            <option value="starter">Starter</option>
                            <option value="professional">Professional</option>
                            <option value="pro">Pro</option>
                            <option value="team">Team</option>
                          </select>
                        </div>
                        <div className="form-row">
                          <label>Notes</label>
                          <input
                            type="text"
                            value={editForm.notes || ''}
                            onChange={e => setEditForm({...editForm, notes: e.target.value})}
                            placeholder="Optional notes"
                          />
                        </div>
                        <div className="form-actions">
                          <button 
                            className="save-btn"
                            onClick={() => handleUpdateCost(service.id)}
                          >
                            💾 Save
                          </button>
                          <button 
                            className="cancel-btn"
                            onClick={() => {
                              setEditingService(null);
                              setEditForm({});
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="current-values">
                        <div className="value-row">
                          <span>Current:</span>
                          <span>{formatCurrency(service.currentCost?.currentAmount)}</span>
                        </div>
                        <div className="value-row">
                          <span>Tier:</span>
                          <span className={getTierClass(service.tier)}>{service.tier}</span>
                        </div>
                        <div className="value-row">
                          <span>Last Updated:</span>
                          <span>{formatDate(service.currentCost?.updatedAt) || 'Never'}</span>
                        </div>
                        <button 
                          className="edit-btn"
                          onClick={() => {
                            setEditingService(service.id);
                            setEditForm({
                              currentAmount: service.currentCost?.currentAmount || 0,
                              projectedAmount: service.currentCost?.projectedAmount || 0,
                              usagePercent: service.currentCost?.usagePercent || 0,
                              tier: service.tier,
                              notes: service.currentCost?.notes || ''
                            });
                          }}
                        >
                          ✏️ Update
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Quick Reference */}
      <div className="quick-reference">
        <h3>🔗 Quick Console Links</h3>
        <div className="console-links">
          {services.map(service => (
            <a 
              key={service.id}
              href={service.consoleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="console-link-btn"
            >
              {SERVICE_CONFIG[service.id]?.icon} {service.name}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CloudServicesDashboard;
